import { Router } from "next/router"
import { useInterpret } from "@xstate/react"
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react"
import * as Occupancy from "@ssc-booking/api-contracts/src/occupancy"
import { inspect } from "@xstate/inspect"
import * as UUID from "@ssc-booking/functional-support/src/uuid"
import { match } from "ts-pattern"
import * as F from "fp-ts/function"
import * as E from "fp-ts/Either"
import { buildECommerceMachine } from "../../features/_common/machines/ecommerce-machine/e-commerce-machine"
import { RequestResponseChannel } from "../machines/data-load-machine"
import { IsomorphicRequestResponseChannel } from "../api/isomorphic-request-response-channel"
import { WebSocketClient } from "../api/web-socket-client"
import { MachinesContextType } from "../machines/machines-context"
import notificationMachine from "../machines/notification-machine"
import { AppBaseData, CruiseEnabled } from "../app-data"
import { ECommerceV4Flow, PriceConfigV4Flow } from "../../features/_common/machines/flow-version"
import { WebAppConfig } from "../web-app-config"
import { SuiteNumberValidator } from "../../features/_common/suite-number-validator"
import { SuiteCategoryValidator } from "../../features/_common/suite-category-validator"
import { SanityContent } from "../getServerSidePropsWithDefaultSanityContent"
import { switchFlowMachine } from "../../features/_common/machines/switch-flow-machine"
import { submitStatusMachine } from "../../features/_common/machines/submit-status-machine"
import { workQueueMachine } from "../machines/work-queue/work-queue-machine"
import { Geolocation } from "../providers/geolocation-provider"
import { MachineServices } from "../machines/machine-services"
import { ECommerceContext } from "../../features/_common/machines/ecommerce-machine/e-commerce-machine-context"
import { BookingRouter, getFromNormalQuery } from "../routing"
import { AppVersion } from "../app-version"
import { ChannelStatus } from "../api/channel-status"
import { ServerNotificationChannel } from "../machines/server-notification-machine/server-notification-machine"
import { IsomorphicServerNotificationChannel } from "../api/isomorphic-server-notification-channel"

export type MandatorySanityContent = SanityContent<"penaltiesModalContent">

export const useCreateServices = (
    router: Router,
    appData: AppBaseData,
    sanityContent: MandatorySanityContent,
    geoLocation: Geolocation,
    config: WebAppConfig,
): MachinesContextType => {
    const { NODE_ENV, wsUrl } = config
    const machinesContextRef = useRef<MachinesContextType>()

    useXStateInspect(NODE_ENV)

    const notificationService = useInterpret(notificationMachine)
    const submitStatus = useInterpret(submitStatusMachine)
    const workQueue = useInterpret(workQueueMachine)
    const ecommerceServices = { notification: notificationService, submitStatus }
    const ecommerceContext = makeECommerceContext(
        appData,
        sanityContent,
        ecommerceServices,
        geoLocation,
        config,
        router,
        machinesContextRef,
    )
    const [requestResponseChannel, serverNotificationChannel] = useIsomorphicChannels(wsUrl, ecommerceContext.sessionId)
    const ecommerceMachine = useMemo(
        () => buildECommerceMachine(requestResponseChannel, serverNotificationChannel, ecommerceContext),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [requestResponseChannel],
    )
    const ecommerceService = useInterpret(ecommerceMachine, {
        context: ecommerceContext,
        devTools: NODE_ENV == "development",
    })
    const switchFlow = useInterpret(switchFlowMachine)
    const context = {
        ecommerce: ecommerceService,
        notification: notificationService,
        channel: requestResponseChannel,
        serverNotificationChannel,
        switchFlow,
        workQueue,
        submitStatus,
    }

    machinesContextRef.current = context
    return context
}

const useXStateInspect = (NODE_ENV: string | null) =>
    useEffect(() => {
        if (NODE_ENV === "development" && window.location.search?.includes("inspect")) {
            inspect({
                iframe: false,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
const makeECommerceContext = (
    { cruiseCode, locale, cruiseEnabled, appVersion }: AppBaseData,
    sanityContent: MandatorySanityContent,
    services: MachineServices,
    geoLocation: Geolocation,
    config: WebAppConfig,
    router: BookingRouter,
    machinesContextRef: MutableRefObject<MachinesContextType | undefined>,
): ECommerceContext => {
    const flowVersion = toFlowVersion(cruiseEnabled, appVersion, config, machinesContextRef)
    const sessionId = F.pipe(
        getFromNormalQuery(router, "checkout"),
        UUID.UUID.strictType.decode,
        E.getOrElse(() => UUID.new()),
    )
    return {
        guests: Occupancy.of(2),
        cruiseCode,
        locale,
        suitesSanity: sanityContent.cruiseData.ship.version.suites,
        services,
        sanityContent,
        geoLocation,
        sessionId,
        fareCards: [],
        paymentTerms: {},
        cruiseGroup: sanityContent.cruiseData.cruiseGroup,
        vsCode: { type: "none", maybeVsMember: false },
        checkoutStatus: { status: "tobeValidated" },
        activeStep: flowVersion.steps.initialActiveStep,
        flowVersion: flowVersion,
        config,
        suiteNumberValidator: new SuiteNumberValidator(),
        suiteCategoryValidator: new SuiteCategoryValidator(),
        router,
        isAutomaticSelection: false,
        isRestoreCompleted: false,
    }
}

type Websocket = RequestResponseChannel & ServerNotificationChannel
const useIsomorphicChannels = (url: string, sessionId: string) => {
    const wsUrl = websocketUrl(url, sessionId)
    const webSocketClient = useRef<Websocket | undefined>(undefined)
    const requestResponseChannel = useMemo(() => new IsomorphicRequestResponseChannel(webSocketClient), [])
    const serverNotificationChannel = useMemo(() => new IsomorphicServerNotificationChannel(webSocketClient), [])
    const [, setStatusRR] = useState<ChannelStatus>(requestResponseChannel.status()) //HACK Force re-rendering
    const [, setStatusSN] = useState<ChannelStatus>(serverNotificationChannel.status()) //HACK Force re-rendering

    useEffect(() => {
        webSocketClient.current = new WebSocketClient(wsUrl, sessionId)
        setStatusRR(requestResponseChannel.status())
        setStatusSN(serverNotificationChannel.status())
    }, [requestResponseChannel, setStatusRR, serverNotificationChannel, setStatusSN])

    return [requestResponseChannel, serverNotificationChannel] as const
}
const toFlowVersion = (
    cruiseEnabled: CruiseEnabled,
    version: AppVersion,
    config: WebAppConfig,
    machinesContextRef: MutableRefObject<MachinesContextType | undefined>,
) =>
    match([cruiseEnabled, version] as const)
        .with(["ECommerceEnabled", "v4"], () => ECommerceV4Flow(config, machinesContextRef))
        .with(["PriceConfigEnabled", "v4"], () => PriceConfigV4Flow(config, machinesContextRef))
        .exhaustive()

const websocketUrl = (url: string, sessionId: string) => {
    const parsedUrl = new URL(url)
    parsedUrl.searchParams.set("sessionId", sessionId)
    return parsedUrl.href
}
